import { useEffect } from "react";

import { useIntl } from "react-intl";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";
import {
    AnchorPageRelayQuery,
    shapeAnchorContentfulResponse
} from "src/contentful";

import { anchorPageRelayQuery } from "@/__generated_contentful__/anchorPageRelayQuery.graphql";
import { type VideoCarouselProps } from "@/components/confluence/carousel/src";
import { useLogMeInD0to6 } from "@/components/confluence/hooks/use-log-me-in-d0to6.tsx";
import { PaidHeroVideoCarousel } from "@/patterns/paid-hero/src/paid-hero-video-carousel";
import HeroSection from "@/templates/software/confluence/confluence-software-page/src/sections/log-me-in/hero";
import {
    AnchorContentsResponse,
    PaidLandingPersonaTypes
} from "@/utils/anchor-queries/get-anchor-contents";
import { experiments, useExperimentExposure } from "@/utils/experiment";
import { FeatureExposed } from "@/utils/feature-exposed";

import { ConfluenceSoftwarePageProps } from "../confluence-software-page.tsx";
import { ConfluencePagePaidLpConflAnchor } from "../paid-lp-confl-anchor.tsx";
import { isTargetedSubAnchor } from "../subAnchors/subAnchor-util.tsx";

export interface ConfluencePagePaidLpConfFeaturesRelayProps
    extends ConfluenceSoftwarePageProps {
    anchorContents?: AnchorContentsResponse | null;
    persona?: string;
    queryRef?: PreloadedQuery<anchorPageRelayQuery>;
}

export const ConfluenceSoftwarePageVariantPaidLpConfFeaturesRelay = (
    pageProps: ConfluencePagePaidLpConfFeaturesRelayProps
) => {
    const intl = useIntl();
    const [exposureSubAnchorFired, fireSubAnchorExposure] =
        useExperimentExposure(experiments.cfind3254LandingPageSubAnchor);
    const [brandAnchorExposureFired, fireBrandAnchorExposure] =
        useExperimentExposure(experiments.cfind3627BrandAnchorVisualCleanup);
    useEffect(() => {
        if (
            isTargetedSubAnchor(pageProps.subAnchor) &&
            !exposureSubAnchorFired
        ) {
            fireSubAnchorExposure();
        }
    }, [exposureSubAnchorFired, fireSubAnchorExposure]);

    useEffect(() => {
        if (!brandAnchorExposureFired) {
            fireBrandAnchorExposure();
        }
    }, [brandAnchorExposureFired, fireBrandAnchorExposure]);

    const data = usePreloadedQuery(
        AnchorPageRelayQuery,
        pageProps.queryRef ||
            (null as unknown as PreloadedQuery<anchorPageRelayQuery>)
    );
    pageProps = Object.assign({}, pageProps, {
        anchorContents: shapeAnchorContentfulResponse(data)
    });

    const { userProfileName, recentSites, shouldShowLogMeIn } = useLogMeInD0to6(
        {
            availableProducts: pageProps.availableProducts,
            userProfile: pageProps.userProfile
        }
    );

    const carouselSlides = [
        {
            primaryText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.FeaturesSection.start-with-page.title",
                defaultMessage: "Start with a page"
            }),
            description: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.FeaturesSection.start-with-page.body",
                defaultMessage:
                    "Plan projects, take notes, and brainstorm. Let AI write your first draft."
            }),

            videoSrcMp4:
                // eslint-disable-next-line local/no-non-orangelogic-images-eslint
                "https://wac-cdn.atlassian.com/misc-assets/videos/confluence-product-tour/PT_CT_Pages_LowBR_reencode.mp4",
            videoSrcWebm:
                // eslint-disable-next-line local/no-non-orangelogic-images-eslint
                "https://wac-cdn.atlassian.com/misc-assets/videos/confluence-product-tour/PT_CT_Pages_LowBR.webm",
            videoPreviewSrc:
                // eslint-disable-next-line local/no-non-orangelogic-images-eslint
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/PT_CT_Pages_Preview.webp"
        },
        {
            primaryText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.FeaturesSection.collaborate-with-teams.title",
                defaultMessage: "Collaborate with your teams"
            }),
            description: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.FeaturesSection.collaborate-with-teams.body",
                defaultMessage:
                    "Invite your teams to collaborate using real-time editing and collaborative whiteboards."
            }),

            videoSrcMp4:
                // eslint-disable-next-line local/no-non-orangelogic-images-eslint
                "https://wac-cdn.atlassian.com/misc-assets/videos/confluence-product-tour/PT_CT_Whiteboards_LowBR_reencode.mp4",
            videoSrcWebm:
                // eslint-disable-next-line local/no-non-orangelogic-images-eslint
                "https://wac-cdn.atlassian.com/misc-assets/videos/confluence-product-tour/PT_CT_Whiteboards_LowBR.webm",
            videoPreviewSrc:
                // eslint-disable-next-line local/no-non-orangelogic-images-eslint
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/PT_CT_Whiteboards_Preview.webp"
        },
        {
            primaryText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.FeaturesSection.connect-work.title",
                defaultMessage: "Connect all your work"
            }),
            description: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.FeaturesSection.connect-work.body",
                defaultMessage:
                    "Stay organized in dedicated workspaces, equipped with AI-powered search."
            }),

            videoSrcMp4:
                // eslint-disable-next-line local/no-non-orangelogic-images-eslint
                "https://wac-cdn.atlassian.com/misc-assets/videos/confluence-product-tour/PT_CT_Spaces_LowBR_reencode.mp4",
            videoSrcWebm:
                // eslint-disable-next-line local/no-non-orangelogic-images-eslint
                "https://wac-cdn.atlassian.com/misc-assets/videos/confluence-product-tour/PT_CT_Spaces_LowBR.webm",
            videoPreviewSrc:
                // eslint-disable-next-line local/no-non-orangelogic-images-eslint
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/PT_CT_Spaces_Preview.webp"
        }
    ];

    const carouselHeroProps: VideoCarouselProps = {
        "aria-label": "A display of various confluence features",
        "id": "confluence-template-video-carousel",
        "slides": carouselSlides
    };

    const isLoggedIn = !!userProfileName;
    let customHeroSection;

    if (isLoggedIn) {
        if (shouldShowLogMeIn) {
            // A logged in d0to6 customer
            customHeroSection = (
                <FeatureExposed
                    experimentKey="cfind-2801-get-paid-users-back-to-product"
                    group="Test"
                >
                    <HeroSection
                        confluenceSites={recentSites}
                        userProfileName={userProfileName}
                        hideExploreMore
                        loggedInPaid
                    />
                </FeatureExposed>
            );
        } else {
            // A logged in customer that's not d0to6
            customHeroSection = (
                <PaidHeroVideoCarousel
                    pageId="confluence-default"
                    embeddedSignup={{ bundle: "confluence" }}
                    carouselSlides={carouselSlides}
                    shouldUseNewBrandBackground
                    shouldUseTransparentBackground
                />
            );
        }
    } else {
        // Logged out user
        customHeroSection = null;
    }

    return (
        <ConfluencePagePaidLpConflAnchor
            {...pageProps}
            pageId="paid-lp-conf-features"
            persona={"unknown" as PaidLandingPersonaTypes}
            pageVariant="brandUpdate"
            dataProductKey="confluence-PaidLpConflFeatures"
            keyword={pageProps.keyword}
            carouselHeroProps={carouselHeroProps}
            customHeroSection={customHeroSection}
        />
    );
};
